import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import useStore from '../store';
import { isIOS, isNative } from '../services/platform';
import { PRODUCT_ID_MAP, purchaseProduct, restorePurchases, whenIAPReady, setOnPurchaseUpdate } from '../services/iap';
import { openExternal } from '../services/openLink';

export default function Premium() {
  const navigate = useNavigate();
  const { fetchSubscription } = useStore();
  const isPremium = useStore((s) => s.isPremium);
  const [loading, setLoading] = useState(null);
  const [iapReady, setIapReady] = useState(false);
  const useApplePay = isNative() && isIOS();

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  useEffect(() => {
    if (!useApplePay) {
      setIapReady(true);
      return;
    }
    let cancelled = false;
    whenIAPReady().then(() => {
      if (!cancelled) setIapReady(true);
    });
    setOnPurchaseUpdate((update) => {
      if (update?.refetch) {
        // The verified handler's apple-verify write(s) have settled — read the
        // row back now (never before, or the paywall snaps back to Free).
        fetchSubscription();
        return;
      }
      // CdvPurchase fired `approved`. Optimistically refresh the paywall — the
      // verified handler will POST /apple-verify next, and the second
      // fetchSubscription a few seconds later picks up that write.
      //
      // NOT on a restore: StoreKit re-delivers every historical transaction and
      // this bridge never sends an expiry, so a lapsed subscriber would be told
      // "Welcome to Premium!" once per past renewal and then land back on Free.
      // handleRestore reports the real outcome from the refetched row instead.
      if (!update?.restored) toast.success('Welcome to Premium!');
      fetchSubscription();
      setTimeout(() => fetchSubscription(), 4000);
    });
    return () => {
      cancelled = true;
      setOnPurchaseUpdate(null);
    };
  }, [useApplePay, fetchSubscription]);

  const isPremiumUser = isPremium();

  async function handleCheckout(priceType) {
    setLoading(priceType);
    try {
      if (useApplePay) {
        const productKey = priceType === 'monthly' ? 'premium_month' : priceType === 'yearly' ? 'premium_year' : null;
        if (!productKey) throw new Error('This plan is only available on the web.');
        const productId = PRODUCT_ID_MAP[productKey];
        await purchaseProduct(productId);
        // CdvPurchase approved() handler verifies + finishes; subscription state will refresh on next fetch.
      } else {
        throw new Error('Purchases are only available in the iOS app.');
      }
    } catch (err) {
      // shared-docs sentinel: iap.js throws 'canceled' (err.cancelled) for the
      // whole StoreKit cancel family — 6777006 / 6777003 / 6777010. Never toast
      // a cancel on a purchase control (Guideline 2.1(b)).
      if (err.message !== 'canceled' && !err.cancelled) {
        toast.error(err.message || 'Purchase failed');
      }
    } finally {
      setLoading(null);
    }
  }

  async function handleRestore() {
    setLoading('restore');
    try {
      // restorePurchases only *triggers* re-delivery; receipts flow back through
      // the async approved/verified handlers, and an `approved` for a restored
      // transaction proves nothing (StoreKit replays expired ones too). Only the
      // refetched backend row — which enforces expiry — can say whether this
      // Apple ID actually has a live subscription, so the outcome is reported
      // once, here, after the polls.
      await restorePurchases();
      toast('Checking your purchases…', { id: 'restore' });
      await fetchSubscription();
      setTimeout(() => fetchSubscription(), 4000);
      setTimeout(() => {
        if (useStore.getState().isPremium()) {
          toast.success('Welcome to Premium!', { id: 'restore' });
        } else {
          toast('No active purchases found on this Apple ID.', { id: 'restore' });
        }
      }, 6000);
    } catch (err) {
      toast.error(err.message || 'Restore failed');
    } finally {
      setLoading(null);
    }
  }

  const features = [
    { free: '3 habits', premium: 'Unlimited habits' },
    { free: 'Basic streaks', premium: 'Advanced streaks + freeze' },
    { free: 'Simple stats', premium: 'Detailed analytics' },
    { free: '30-day calendar', premium: 'Full year calendar' },
    { free: null, premium: 'Data export (CSV/JSON)' },
    { free: null, premium: 'Priority support' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center gap-3 shadow-sm safe-area-top">
        <button
          onClick={() => navigate(-1)}
          className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-bold text-gray-900 dark:text-white">Premium</h1>
      </div>

      <div className="px-4 py-6 max-w-lg mx-auto">
        {/* Hero */}
        <div className="text-center mb-6">
          <div className="text-5xl mb-3">✨</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Unlock Your Full Potential
          </h2>
          <p className="text-gray-500 dark:text-gray-400">
            Track unlimited habits and get deeper insights into your progress.
          </p>
        </div>

        {/* Already premium */}
        {isPremiumUser && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-4 mb-6 text-center">
            <p className="text-green-800 dark:text-green-300 font-medium mb-3">
              You're on the Premium plan!
            </p>
          </div>
        )}

        {/* Pricing Cards */}
        {!isPremiumUser && (
          <div className="space-y-3 mb-4">
            {/* Monthly */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Premium Monthly</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">£4.99</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">/month</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Auto-renewing subscription</p>
                </div>
                <button
                  onClick={() => handleCheckout('monthly')}
                  disabled={loading === 'monthly' || !iapReady}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl text-sm font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                >
                  {loading === 'monthly' ? '...' : !iapReady ? 'Loading…' : 'Subscribe'}
                </button>
              </div>
            </div>

            {/* Yearly — Most Popular */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border-2 border-primary-500 relative">
              <div className="absolute -top-3 left-4 px-3 py-0.5 bg-primary-600 text-white text-xs font-bold rounded-full">
                Best Value
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Premium Yearly</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900 dark:text-white">£19.99</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">/year</span>
                  </div>
                  <p className="text-xs text-green-600 dark:text-green-400 font-medium mt-0.5">
                    Auto-renewing subscription · just £1.67/month
                  </p>
                </div>
                <button
                  onClick={() => handleCheckout('yearly')}
                  disabled={loading === 'yearly' || !iapReady}
                  className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {loading === 'yearly' ? '...' : !iapReady ? 'Loading…' : 'Subscribe'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Subscription terms + legal links — directly below the buy buttons (Guideline 3.1.2(c)) */}
        {!isPremiumUser && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 mb-6 text-xs text-gray-600 dark:text-gray-400 space-y-2">
            <p>
              {useApplePay ? (
                <>
                  Payment will be charged to your Apple ID at confirmation of purchase. Subscriptions auto-renew
                  at the same price unless cancelled at least 24 hours before the end of the current period.
                  Manage or cancel any time in Settings → [your name] → Subscriptions.
                </>
              ) : (
                <>
                  Premium subscriptions are available in the Streaks iOS app. Download it from the App Store
                  to unlock unlimited habits and premium features.
                </>
              )}
            </p>
            <p className="text-center pt-1">
              <button
                type="button"
                onClick={() => openExternal('/terms.html')}
                className="text-primary-600 dark:text-primary-400 underline font-medium"
              >
                Terms of Use (EULA)
              </button>
              {' · '}
              <button
                type="button"
                onClick={() => openExternal('/privacy.html')}
                className="text-primary-600 dark:text-primary-400 underline font-medium"
              >
                Privacy Policy
              </button>
            </p>
            {useApplePay && (
              <p className="text-center pt-1">
                <button
                  onClick={handleRestore}
                  disabled={loading === 'restore'}
                  className="text-primary-600 dark:text-primary-400 underline font-medium disabled:opacity-50"
                >
                  {loading === 'restore' ? 'Restoring...' : 'Restore Purchases'}
                </button>
              </p>
            )}
          </div>
        )}

        {/* Features comparison */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden mb-6">
          <div className="grid grid-cols-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <div />
            <p className="text-xs font-medium text-gray-400 text-center">Free</p>
            <p className="text-xs font-medium text-primary-600 text-center">Premium</p>
          </div>
          {features.map((feature, i) => (
            <div
              key={i}
              className="grid grid-cols-3 px-4 py-2.5 border-b border-gray-50 dark:border-gray-700/50 last:border-0"
            >
              <div />
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                {feature.free || '—'}
              </p>
              <p className="text-xs text-gray-900 dark:text-white font-medium text-center">
                {feature.premium}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
